/**
 * The board's viewport, wrapped in the ViewportState contract.
 *
 * Every conversion here delegates to packages/shared/src/viewport.ts. That module is
 * the single implementation of screen <-> canvas maths (docs/04-MODULE-CONTRACTS.md
 * §3.3.1), and the zoom-adaptive thickness in SRS C3 depends on there being exactly
 * one. Nothing in this file may compute a conversion of its own.
 */

import type { ViewportState } from '@opal/contracts';
import {
  canvasToScreen,
  screenToCanvas,
  screenWidthToCanvasWidth,
  type Point,
  type Viewport,
} from '@opal/shared';

/** A fresh board: 100% zoom, origin at the top-left, size filled in on first layout. */
export const INITIAL_VIEWPORT: Viewport = {
  zoom: 1,
  panX: 0,
  panY: 0,
  widthPx: 0,
  heightPx: 0,
};

/** Presents a plain viewport through the contract every module reads. */
export function createViewportState(viewport: Viewport): ViewportState {
  return {
    zoom: viewport.zoom,
    panX: viewport.panX,
    panY: viewport.panY,
    widthPx: viewport.widthPx,
    heightPx: viewport.heightPx,
    screenToCanvas: (point: Point) => screenToCanvas(viewport, point),
    canvasToScreen: (point: Point) => canvasToScreen(viewport, point),
    screenWidthToCanvasWidth: (screenPx: number) => screenWidthToCanvasWidth(viewport, screenPx),
  };
}

/** The plain viewport behind a ViewportState, for code that needs the shared helpers. */
export function toViewport(state: ViewportState): Viewport {
  return {
    zoom: state.zoom,
    panX: state.panX,
    panY: state.panY,
    widthPx: state.widthPx,
    heightPx: state.heightPx,
  };
}
